import type { ClientMsg, ServerMsg } from '@tanky/shared';
import { setServerNow } from './clock';

type Listener = (msg: ServerMsg) => void;

class Socket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private openListeners = new Set<() => void>();
  private gameId: string | null = null;
  private retry = 0;
  private timer: number | undefined;
  private wanted = false;

  connect() {
    this.wanted = true;
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      if (this.gameId) this.send({ t: 'sub', gameId: this.gameId });
      for (const l of this.openListeners) l();
    };
    ws.onmessage = (e) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      if (msg.t === 'hello' || msg.t === 'turn') setServerNow(msg.serverNow);
      for (const l of this.listeners) l(msg);
    };
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      if (!this.wanted) return;
      const delay = Math.min(10000, 500 * 2 ** this.retry++);
      clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.connect(), delay);
    };
  }

  disconnect() {
    this.wanted = false;
    clearTimeout(this.timer);
    this.ws?.close();
    this.ws = null;
  }

  get connected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  send(msg: ClientMsg) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  setGame(gameId: string | null) {
    this.gameId = gameId;
    this.send(gameId ? { t: 'sub', gameId } : { t: 'unsub' });
  }

  on(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  onOpen(l: () => void): () => void {
    this.openListeners.add(l);
    return () => this.openListeners.delete(l);
  }
}

export const socket = new Socket();

// Po návratu do aplikace (mobil ji uspí) se hned znovu připojit
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') socket.connect();
  });
}
